/**
 * Contracts Tab Component - Shows contracts for the supplier
 * Clicking a contract opens the detail modal with Parts and History tabs
 */

import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Loader2, FileText, Calendar, DollarSign, ExternalLink, User, RefreshCw, Plus } from 'lucide-react';
import { purchasingAPI, ContractPrice, SupplierProfile } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import ContractPriceDetailModal from '../../components/purchasing/ContractPriceDetailModal';
import ContractEditModal from '../../components/purchasing/ContractEditModal';
import MasterContractCreateModal from '../../components/purchasing/MasterContractCreateModal';

interface ContractsTabProps {
  supplier: SupplierProfile;
}

export function ContractsTab({ supplier }: ContractsTabProps) {
  const { formatDate } = useFormatters();
  const [contracts, setContracts] = useState<ContractPrice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedContractId, setSelectedContractId] = useState<number | null>(null);
  const [editingContract, setEditingContract] = useState<ContractPrice | null>(null);
  const [creatingContract, setCreatingContract] = useState(false);

  useEffect(() => {
    fetchContracts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier.company]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getContractPrices({
        supplier: supplier.company,
        ordering: '-valid_from',
      });
      setContracts(response.data.results);
    } catch (err: unknown) {
      console.error('Failed to fetch contracts:', err);
      setError('Failed to load contracts');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'expired':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (currency: string) => {
    const currencySymbols: Record<string, string> = {
      GBP: '£',
      USD: '$',
      EUR: '€',
      JPY: '¥',
      CNY: '¥',
      INR: '₹',
      AUD: 'A$',
      CAD: 'C$',
      CHF: 'CHF',
      MXN: 'MX$',
    };
    return currencySymbols[currency] || currency;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Contracts
            {!loading && (
              <Badge variant="outline" className="ml-2">
                {contracts.length} {contracts.length === 1 ? 'contract' : 'contracts'}
              </Badge>
            )}
          </CardTitle>
          {/* CR-5770: Create high-level supplier contract award */}
          <Button size="sm" onClick={() => setCreatingContract(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Contract
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-600">
            <p>{error}</p>
            <button
              onClick={fetchContracts}
              className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
            >
              Retry
            </button>
          </div>
        ) : contracts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No contracts found for this supplier</p>
            <p className="text-sm mt-2">
              Contracts capture the high-level commercial agreement, lifecycle and master document.
            </p>
            <Button size="sm" className="mt-4" onClick={() => setCreatingContract(true)}>
              <Plus className="h-4 w-4 mr-1" /> Create the first contract
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {contracts.map((contract) => (
              <div
                key={contract.id}
                className="border rounded-lg p-4 hover:bg-accent/50 transition-colors cursor-pointer group"
                onClick={() => setSelectedContractId(contract.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && setSelectedContractId(contract.id)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-lg">{contract.contract_number}</p>
                      <ExternalLink className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Customer: {contract.customer_name}
                    </p>
                  </div>
                  <Badge className={getStatusBadgeColor(contract.status)}>
                    {contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}
                  </Badge>
                </div>

                {/* CR-4879: Parts field removed from contract summary card —
                    high-level detail view should only carry key contract
                    information, not the parts count. */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground block">Valid From</span>
                      <span className="font-medium">{formatDate(contract.valid_from)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground block">Valid To</span>
                      <span className="font-medium">{formatDate(contract.valid_to)}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground block">Currency</span>
                      <span className="font-medium">{formatCurrency(contract.currency)} ({contract.currency})</span>
                    </div>
                  </div>
                  {/* CR-4061: Contract Owner */}
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground block">Contract Owner</span>
                      <span className="font-medium">{contract.owner_name || 'Not assigned'}</span>
                    </div>
                  </div>
                  {/* CR-4058: Auto-Renew Status */}
                  <div className="flex items-center gap-2">
                    <RefreshCw className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <span className="text-muted-foreground block">Auto-Renew</span>
                      <span className={`font-medium ${contract.auto_renew ? 'text-green-600' : 'text-muted-foreground'}`}>
                        {contract.auto_renew ? 'Yes' : 'No'}
                      </span>
                    </div>
                  </div>
                </div>

                {contract.notes && (
                  <div className="mt-3 pt-3 border-t">
                    <p className="text-sm text-muted-foreground">{contract.notes}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Contract Detail Modal */}
        {selectedContractId && (
          <ContractPriceDetailModal
            contractId={selectedContractId}
            isOpen={true}
            onClose={() => setSelectedContractId(null)}
            onEdit={(contract) => {
              setSelectedContractId(null);
              setEditingContract(contract);
            }}
          />
        )}
        {/* CR-4060: Contract Edit Modal */}
        {editingContract && (
          <ContractEditModal
            contract={editingContract}
            isOpen={true}
            onClose={() => setEditingContract(null)}
            onSaved={() => {
              setEditingContract(null);
              fetchContracts();
            }}
          />
        )}
        {/* CR-5770: High-level Master Contract create modal */}
        {creatingContract && (
          <MasterContractCreateModal
            supplierId={supplier.company}
            supplierName={supplier.company_name || 'this supplier'}
            isOpen={true}
            onClose={() => setCreatingContract(false)}
            onCreated={() => {
              setCreatingContract(false);
              fetchContracts();
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
